import { filterFns } from '@tanstack/react-table';

import { filterBySelectedCode, filterByStatus } from '@/pages/Catalog/utils/filters';

import { sortAsFrequency, sortAsNumber, sortAsPeriodType } from './utils/sorts';

export const ADDITIONAL_ITEM = 'AdditionalItem';
export type CountedLabelFunction = (
  key: string,
  joined?: boolean
) => {
  label: string;
  count: number;
}[];

export const FILTER_KEYS = {
  COMMODITY: 'Commodity',
  TRADING_HUB: 'TradingHub',
  DELIVERY_BASIS: 'DeliveryBasis',
  PRICING_BASIS: 'PricingBasis',
  UNITS: 'Units',
  CURRENCY: 'Currency',
  FREQUENCY: 'Frequency',
  INDEX_MODULE: 'IndexModule'
} as const;

export type FilterKey = (typeof FILTER_KEYS)[keyof typeof FILTER_KEYS];

export const labeledFilterColumns: Record<
  FilterKey,
  (getCountedLabels: CountedLabelFunction) => ReturnType<CountedLabelFunction>
> = {
  [FILTER_KEYS.COMMODITY]: getCountedLabels => getCountedLabels(FILTER_KEYS.COMMODITY),
  [FILTER_KEYS.TRADING_HUB]: getCountedLabels => getCountedLabels(FILTER_KEYS.TRADING_HUB),
  [FILTER_KEYS.DELIVERY_BASIS]: getCountedLabels => getCountedLabels(FILTER_KEYS.DELIVERY_BASIS),
  [FILTER_KEYS.PRICING_BASIS]: getCountedLabels => getCountedLabels(FILTER_KEYS.PRICING_BASIS),
  [FILTER_KEYS.UNITS]: getCountedLabels => getCountedLabels(FILTER_KEYS.UNITS),
  [FILTER_KEYS.CURRENCY]: getCountedLabels => getCountedLabels(FILTER_KEYS.CURRENCY),
  [FILTER_KEYS.FREQUENCY]: getCountedLabels => getCountedLabels(FILTER_KEYS.FREQUENCY),
  [FILTER_KEYS.INDEX_MODULE]: getCountedLabels => getCountedLabels(FILTER_KEYS.INDEX_MODULE, true)
};
export const oilOnlyModules = [
  'AfricaCrude',
  'AmericasMarine',
  'AsiaBiofuels',
  'AsiaCrude',
  'AsiaFeedstocks',
  'AsiaFuelOil',
  'AsiaGasoil',
  'AsiaGasoline',
  'AsiaJet',
  'AsiaLPG',
  'AsiaMarine',
  'AsiaNaphtha',
  'EuropeanBiofuels',
  'EuropeanCrude',
  'EuropeanFeedstocks',
  'EuropeanFuelOil',
  'EuropeanGasoil',
  'EuropeanGasoline',
  'EuropeanJet',
  'EuropeanLPG',
  'EuropeanMarine',
  'EuropeanNaphtha',
  'GlobalIndexes',
  'GlobalOilSelect',
  'MiddleEastCrude',
  'MiddleEastFuelOil',
  'MiddleEastGasoil',
  'MiddleEastGasoline',
  'MiddleEastJet',
  'MiddleEastNaphtha',
  'NorthAmericaBiofuels',
  'NorthAmericaCrude',
  'NorthAmericaDistillate',
  'NorthAmericaFeedstocks',
  'NorthAmericaFuelOil',
  'NorthAmericaGasoline',
  'NorthAmericaJet',
  'NorthAmericaNaphtha',
  'NorthAmericaNGLs',
  'NorthAmericaStorage',
  'SouthAmericaCrude'
];

export const energyTransitionModules = [
  'EuropeanSAF',
  'AsiaSAF',
  'NorthAmericaSAF',
  'AsiaBiofuels',
  'EuropeanBiofuels',
  'GlobalHydrogen',
  'GlobalMarineCarbon',
  'GlobalVoluntaryCarbon',
  'NorthAmericaBiofuels',
  'AsiaComplianceCarbon'
];

export const spotColumns = [
  { accessorKey: 'Symbol', header: '', size: 20, enableGlobalFilter: false },
  {
    accessorKey: 'Status',
    header: '',
    size: 0,
    filterFn: filterByStatus,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'Code',
    header: 'Code',
    size: 100,
    filterFn: filterBySelectedCode
  },
  {
    accessorKey: 'TimeRef',
    header: 'TimeRef',
    size: 95,
    sortingFn: sortAsNumber,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'PeriodType',
    header: 'PeriodType',
    size: 120,
    sortingFn: sortAsPeriodType,
    enableGlobalFilter: false
  },
  { accessorKey: 'Period', header: 'Period', size: 100, enableGlobalFilter: false },
  { accessorKey: 'Alias', header: 'Alias' },
  { accessorKey: 'Title', header: 'Full Title', size: 650 },
  {
    accessorKey: ADDITIONAL_ITEM,
    header: ADDITIONAL_ITEM,
    size: 40,
    enableGlobalFilter: false
  },
  { accessorKey: 'LastDate', header: 'Last Date', size: 100, enableGlobalFilter: false },
  {
    accessorKey: 'Last',
    header: 'Last',
    size: 100,
    sortingFn: sortAsNumber,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'Change',
    header: 'Change',
    size: 100,
    sortingFn: sortAsNumber,
    enableGlobalFilter: false
  },
  { accessorKey: 'Currency', header: 'Currency', size: 100, enableGlobalFilter: false },
  { accessorKey: 'Units', header: 'Units', size: 80, enableGlobalFilter: false },
  { accessorKey: 'Commodity', header: 'Commodity', size: 130, enableGlobalFilter: false },
  { accessorKey: 'TradingHub', header: 'Trading Hub', size: 120, enableGlobalFilter: false },
  { accessorKey: 'DeliveryBasis', header: 'Delivery Basis', size: 150, enableGlobalFilter: false },
  { accessorKey: 'PricingBasis', header: 'Pricing Basis', size: 150, enableGlobalFilter: false },
  {
    accessorKey: 'Frequency',
    header: 'Frequency',
    size: 120,
    sortingFn: sortAsFrequency,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'IndexModule',
    header: 'Index Module',
    size: 200,
    filterFn: filterFns.arrIncludesSome,
    enableGlobalFilter: false
  },
  { accessorKey: 'PeriodAbs', header: 'Period Abs', size: 120, enableGlobalFilter: false },
  { accessorKey: 'StartDate', header: 'Start Date', size: 120, enableGlobalFilter: false },
  { accessorKey: 'EndDate', header: 'End Date', size: 120, enableGlobalFilter: false },
  {
    accessorKey: 'FactsheetVersion',
    header: 'Latest Factsheet',
    size: 170,
    enableGlobalFilter: false
  },
  { accessorKey: 'TimeZone', header: 'Timezone', size: 140, enableGlobalFilter: false },
  {
    accessorKey: 'HolidayCalendar',
    header: 'Holiday Calendar',
    size: 220,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'LastTradeDateCalendar',
    header: 'Last Trade Date Calendar',
    size: 220,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'Increment',
    header: 'Increment',
    size: 110,
    sortingFn: sortAsNumber,
    enableGlobalFilter: false
  },
  { accessorKey: 'Index', header: 'Columns Available', size: 170, enableGlobalFilter: false },
  {
    accessorKey: 'Factsheet',
    header: 'Info',
    size: 50,
    enableGlobalFilter: false
  }
];

export const forwardCurvesColumns = [
  { accessorKey: 'Symbol', header: '', size: 20, enableGlobalFilter: false },
  {
    accessorKey: 'Status',
    header: '',
    size: 0,
    filterFn: filterByStatus,
    enableGlobalFilter: false
  },
  { accessorKey: 'Code', header: 'Code', size: 100 },
  {
    accessorKey: 'TimeRef',
    header: 'TimeRef',
    size: 95,
    sortingFn: sortAsNumber,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'PeriodType',
    header: 'PeriodType',
    size: 120,
    sortingFn: sortAsPeriodType,
    enableGlobalFilter: false
  },
  { accessorKey: 'Periods', header: 'NumPeriods', size: 120, enableGlobalFilter: false },
  { accessorKey: 'Title', header: 'Full Title', size: 550 },
  { accessorKey: 'Currency', header: 'Currency', size: 100, enableGlobalFilter: false },
  { accessorKey: 'Units', header: 'Units', size: 100, enableGlobalFilter: false },
  { accessorKey: 'Commodity', header: 'Commodity', size: 130, enableGlobalFilter: false },
  { accessorKey: 'TradingHub', header: 'Trading Hub', enableGlobalFilter: false },
  { accessorKey: 'DeliveryBasis', header: 'Delivery Basis', size: 150, enableGlobalFilter: false },
  { accessorKey: 'PricingBasis', header: 'Pricing Basis', size: 150, enableGlobalFilter: false },
  { accessorKey: 'Frequency', header: 'Frequency', size: 120, enableGlobalFilter: false },
  {
    accessorKey: 'IndexModule',
    header: 'Index Module',
    size: 200,
    filterFn: filterFns.arrIncludesSome,
    enableGlobalFilter: false
  },
  { accessorKey: 'StartDate', header: 'StartDate', size: 120, enableGlobalFilter: false },
  { accessorKey: 'EndDate', header: 'EndDate', size: 120, enableGlobalFilter: false },
  {
    accessorKey: 'FactsheetVersion',
    header: 'Latest Factsheet',
    size: 170,
    enableGlobalFilter: false
  },
  { accessorKey: 'TimeZone', header: 'Timezone', size: 140, enableGlobalFilter: false },
  {
    accessorKey: 'HolidayCalendar',
    header: 'Holiday Calendar',
    size: 220,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'LastTradeDateCalendar',
    header: 'Last Trade Date Calendar',
    size: 220,
    enableGlobalFilter: false
  },
  { accessorKey: 'Increment', header: 'Increment', size: 110, enableGlobalFilter: false },
  {
    accessorKey: 'Index',
    header: 'Columns Available',
    size: 170,
    sortingFn: sortAsNumber,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'Factsheet',
    header: 'Info',
    size: 50,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'PeriodMin',
    size: 0,
    enableGlobalFilter: false
  },
  {
    accessorKey: 'PeriodMax',
    size: 0,
    enableGlobalFilter: false
  }
];

export const defaultVisibilityState = {
  Status: false,
  TimeRef: false,
  PeriodType: false,
  Period: false,
  StartDate: false,
  EndDate: false,
  FactsheetVersion: false,
  TimeZone: false,
  HolidayCalendar: false,
  LastTradeDateCalendar: false,
  Increment: false,
  Index: false,
  PricingBasis: false,
  DeliveryBasis: false,
  Frequency: false,
  IndexModule: false,
  PeriodMax: false,
  PeriodMin: false,
  PeriodAbs: false
};

export const displayedValueSettingsColumnsList = [
  {
    label: 'Additional Values',
    columns: [
      {
        displayLabel: 'Period Abs',
        accessorKey: 'PeriodAbs'
      }
    ]
  }
];

export const displayedMetadataSettingsColumnsList = [
  {
    label: 'Default columns',
    columns: [
      {
        displayLabel: 'Currency',
        accessorKey: 'Currency'
      },
      {
        displayLabel: 'Units',
        accessorKey: 'Units'
      },
      {
        displayLabel: 'Commodity',
        accessorKey: 'Commodity'
      },
      {
        displayLabel: 'Trading Hub',
        accessorKey: 'TradingHub'
      }
    ]
  },
  {
    label: 'Additional Filters',
    columns: [
      {
        displayLabel: 'Delivery Basis',
        accessorKey: 'DeliveryBasis'
      },
      {
        displayLabel: 'Pricing Basis',
        accessorKey: 'PricingBasis'
      },
      {
        displayLabel: 'Frequency',
        accessorKey: 'Frequency'
      },
      {
        displayLabel: 'Index Module',
        accessorKey: 'IndexModule'
      }
    ]
  },
  {
    label: 'Additional columns',
    columns: [
      {
        displayLabel: 'Start Date',
        accessorKey: 'StartDate'
      },
      {
        displayLabel: 'End Date',
        accessorKey: 'EndDate'
      },
      {
        displayLabel: 'Latest Factsheet',
        accessorKey: 'FactsheetVersion'
      },
      {
        displayLabel: 'Timezone',
        accessorKey: 'TimeZone'
      },
      {
        displayLabel: 'Holiday Calendar',
        accessorKey: 'HolidayCalendar'
      },
      {
        displayLabel: 'Last Trade Date Calendar',
        accessorKey: 'LastTradeDateCalendar'
      },
      {
        displayLabel: 'Increment',
        accessorKey: 'Increment'
      },
      {
        displayLabel: 'Columns Available',
        accessorKey: 'Index'
      }
    ]
  }
];

export const alphabeticallySortedFilters = ['IndexModule'];

export const allColumnIdsToChangeVisibility = [
  'Units',
  'Currency',
  'Commodity',
  'TradingHub',
  'DeliveryBasis',
  'PricingBasis',
  'FactsheetVersion',
  'Frequency',
  'StartDate',
  'EndDate',
  'IndexModule',
  'TimeZone',
  'HolidayCalendar',
  'LastTradeDateCalendar',
  'Increment',
  'Index'
];
